import { useEffect, useState, useSyncExternalStore } from "react";
import { api } from "@/lib/api";
import { onPrefs, pushPref } from "@/lib/prefsSync";

const KEY = "rh.lyrics";
export const loadLyricsOn = () => localStorage.getItem(KEY) !== "off";
export const saveLyricsOn = (on) => { localStorage.setItem(KEY, on ? "on" : "off"); pushPref("lyrics", lyricsPref()); };

/* Per-song timing nudges (seconds, + = lyrics earlier); synced to the account with the on/off switch. */
const OFF_KEY = "rh.lyricsOffsets";
export const OFFSET_STEP = 0.5, OFFSET_MAX = 5;
let offsets = {};
try { offsets = JSON.parse(localStorage.getItem(OFF_KEY) || "{}"); } catch { offsets = {}; }
const offSubs = new Set();
const lyricsPref = () => ({ on: loadLyricsOn(), offsets });
onPrefs("lyrics", {
  apply: (v) => {
    localStorage.setItem(KEY, v?.on === false ? "off" : "on");
    offsets = v?.offsets && typeof v.offsets === "object" ? v.offsets : {};
    localStorage.setItem(OFF_KEY, JSON.stringify(offsets));
    offSubs.forEach((fn) => fn());
  },
  read: lyricsPref,
});
export const clampOffset = (v) => Math.max(-OFFSET_MAX, Math.min(OFFSET_MAX, Math.round(v * 2) / 2));
export const fmtOffset = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(1)}s`;
export const setSongOffset = (id, v) => {
  v = clampOffset(v);
  if (v) offsets[id] = v; else delete offsets[id];
  localStorage.setItem(OFF_KEY, JSON.stringify(offsets));
  offSubs.forEach((fn) => fn());
  pushPref("lyrics", lyricsPref());
};
export const useSongOffset = (id) => useSyncExternalStore((fn) => { offSubs.add(fn); return () => offSubs.delete(fn); }, () => offsets[id] || 0);

/* [mm:ss.xx] lines → [{time, text}] sorted; empty when nothing parses. */
export function parseLrc(lrc) {
  const out = [], re = /\[(\d+):(\d{2})(?:[.:](\d{1,3}))?\]/g;
  for (const line of (lrc || "").split(/\r?\n/)) {
    const text = line.replace(re, "").trim();
    let m;
    re.lastIndex = 0;
    while ((m = re.exec(line))) out.push({ time: Number(m[1]) * 60 + Number(m[2]) + Number(`0.${m[3] || "0"}`), text });
  }
  return out.sort((a, b) => a.time - b.time);
}

const cache = new Map();

/* Lyrics for a library track: {status: idle|loading|ready|none|error, lines[], plain}. Radio/shared-less tracks skip the lookup. */
export function useLyrics(track) {
  const id = track && !track.live && track.artist && track.title ? track.id : null;
  const [state, setState] = useState({ status: "idle" });
  useEffect(() => {
    if (!id) { setState({ status: "idle" }); return; }
    if (cache.has(id)) { setState(cache.get(id)); return; }
    let alive = true;
    setState({ status: "loading" });
    const params = { artist: track.artist, title: track.title, album: track.album || "" };
    if (track.duration >= 1) params.duration = Math.round(track.duration);
    api.get("/lyrics", { params }).then(({ data }) => {
      const lines = data.synced ? parseLrc(data.synced) : [];
      const next = !data.found || (!lines.length && !data.plain)
        ? { status: "none", instrumental: data.instrumental }
        : { status: "ready", lines, plain: data.plain || "" };
      cache.set(id, next);
      if (alive) setState(next);
    }).catch(() => { if (alive) setState({ status: "error" }); });
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}
